import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CompanyRelationNodeDTO, RelationCompanyDTO } from '../../interface/company-corporate-mesh.interface';
import { Edge, Layout, Node } from '@swimlane/ngx-graph';
import * as shape from 'd3-shape';
import { DagreNodesOnlyLayout } from './customDagreNodesOnly';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ServiceInfoClientService } from '../../service/service-info-client.service';
import { pipe, finalize, Subject, tap, concat, last} from 'rxjs';
import Swal from 'sweetalert2';
import { ScoringIngeService } from '../../service/scoring-inge.service';

@Component({
  selector: 'app-company-corporate-mesh-modal',
  templateUrl: './company-corporate-mesh-modal.component.html',
  styleUrls: ['./company-corporate-mesh-modal.component.scss']
})
export class CompanyCorporateMeshModalComponent implements OnInit, OnDestroy{
  public companies: RelationCompanyDTO[];
  public isLoadingMesh: boolean = true;

  public companyTreeRelation: CompanyRelationNodeDTO | null;

  public curve: any = shape.curveBundle.beta(1);
  public layout: Layout = new DagreNodesOnlyLayout();
  public layoutSettings = {
    orientation: 'LR',
    ranker: 'network-simplex'
  };

  public nodes: Node[] = [];
  public links: Edge[] = [];

  public updateGraph$: Subject<any> = new Subject();

  constructor(private dialogRef: MatDialogRef<CompanyCorporateMeshModalComponent>,
    private companyService: ServiceInfoClientService,
    private equifaxScoringService: ScoringIngeService,
    @Inject(MAT_DIALOG_DATA)public data: number 
  ){ 
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public getCompanyRelationships(companyId: number) {
    this.isLoadingMesh = true;
    this.companyService.getCompanyRelationshipMesh(companyId)
    .pipe(
      finalize(() => this.isLoadingMesh = false)
    )
    .subscribe({
      next:(response) => {
        const companyNodes = response.map(c => this.convertToRelationDTO(c));
        this.drawCorporateMesh(companyNodes);
      }
    })
  }

  private convertToRelationDTO(input: CompanyRelationNodeDTO): RelationCompanyDTO
  {
    return {
      companyId: input.companyId,
      companyRut: input.companyRut,
      companyName: input.companyName,
      relatedCompanyId: input.relatedCompanyId,
      isRoot: input.companyId === this.data,
      relationshipType: input.relationshipType
    };
  }

  private drawCorporateMesh(relationships: Array<RelationCompanyDTO>) {
    let updatedMesh: boolean = false;
    for(const company of relationships) {
      const node: Node = {
        id: company.companyId.toString(),
        label: company.companyName,
        data: {
          rut: company.companyRut,
          name: company.companyName,
          relationship: company.relationshipType,
          isRoot: company.isRoot,
          expanded: !company.isRoot,
          relationships: company.relationshipType
        }
      };
      // Avoid adding already existing nodes
      if(!this.nodes.find(n => n.id === node.id)){
        this.nodes.push(node);
        updatedMesh = true;
      }
    }

    for(const company of relationships) {
      if(!company.relatedCompanyId){
        continue;
      }

      const edgeNode = company.relationshipType.find(t => t == 1 || t == 3) 
      ? {
         source: company.relatedCompanyId.toString(),
         target: company.companyId.toString()
        } : 
        {
          source: company.companyId.toString(),
          target: company.relatedCompanyId.toString() 
        }

      const edge: Edge = {
        source: edgeNode.source,
        target: edgeNode.target,
        label: '',
        data: {
          linkText: ''
        }
      }
      if(!this.links.find(l => l.source === edge.source && l.target === edge.target)){
        this.links.push(edge);
        updatedMesh = true;
      }
      
    }
    return updatedMesh;
  }

  

  ngOnInit(): void {
    this.getCompanyRelationships(this.data);
  }

  ngOnDestroy(): void {
    this.updateGraph$.unsubscribe();
  }

  public getStyles(node: Node): any {
    return {
      'padding': '0.3rem',
      'border-radius': '25px',
      'background-color': node.data.isRoot === true ?' lightsalmon' : 
      node.data.relationship.find(t => t == 1 || t == 3)
       ? 'wheat' : 'lightcyan'
    };
  }

  public openRelatedNode(node: Node, requestEquifax: boolean = false) {
    const nodeIdx = this.nodes.findIndex(n => n.id === node.id);
    const companyId = Number.parseInt(node.id);

    if(requestEquifax) {
      Swal.fire({
        title: 'Consultando a Equifax',
        text: 'Espere un momento',
        heightAuto: false,
        icon: 'info',
        didOpen:() =>{
          Swal.showLoading();
        }
      });
    }
    const requestEquifax$ = this.equifaxScoringService
    .getEquifaxReportScoring(node.data.rut)
    .pipe(
      tap(() =>{
        Swal.fire({
          icon: 'success',
          title: 'Consulta exitosa',
          text: 'Se ha realizado exitosamente consulta a Equifax',
        });
      }),
      finalize(() => Swal.hideLoading())
    );
    const requestRelatedCompanies$ = this.companyService.getCompanyRelationshipMesh(companyId)

    const request$ = requestEquifax ? concat(requestEquifax$, requestRelatedCompanies$).pipe(last()) : requestRelatedCompanies$;
    request$.subscribe({
      next:(relations: Array<CompanyRelationNodeDTO>) => {
        const companyNodes = relations.map(c => this.convertToRelationDTO(c));
        const updated = this.drawCorporateMesh(companyNodes);
        if(!updated && !requestEquifax) {
          this.modalToRequestEquifax(node);
        }
        this.updateGraph$.next(true);
      },
      error: (err) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.error.resultMessage || 'No fue posible realizar la consulta'
        })
      }
    });

    // this.companyService.getCompanyRelationshipMesh(companyId)
    // .subscribe({
    //   next:(relations) => {
    //     const companyNodes = relations.map(c => this.convertToRelationDTO(c));
    //     const updated = this.drawCorporateMesh(companyNodes);
    //     if(!updated) {
    //       this.modalToRequestEquifax(node);
    //     }
    //     this.updateGraph$.next(true);
    //   }
    // })
    
  }
  public requestEquifaxFromRoot(){
    const rootNode = this.nodes.find(n => n.data?.isRoot === true);
    if(!rootNode) return;
    this.openRelatedNode(rootNode, true);
  }

  private modalToRequestEquifax(node: Node) {
    Swal.fire({
      icon:'question',
      title: 'No se han encontrado más relaciones',
      text: '¿Desea consultar a Equifax?',
      confirmButtonText: 'Sí',
      denyButtonText: 'No',
      showDenyButton: true,
      showConfirmButton: true
    }).then((r) => {
      if(r.isConfirmed){
        this.openRelatedNode(node, true);
      }
    })
  }
}
